//@ts-nocheck
import React, { useEffect, useState } from 'react';
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import * as d3 from 'd3';
import d3tip from 'd3-tip'
import { schemeCategory10 } from 'd3-scale-chromatic';
import * as Helpers from '../../Common/Helpers';
import { Heading3, ReportButton, ReportText, TextLink, TextSpan } from '../../Common/SharedStyledComponents';
import { downloadNewEmployeeReportData } from "./../../../actions/NewEmployeeReportActions";
import { TitleDiv } from "./Earnings";

const Y_LABEL_MAX = 40;

/** Full y-domain label: "Company Name (count)" */
const companyAxisLabel = (d) => Helpers.toTitleCase(d.company_name) + ' (' + d.count + ')';

/** Truncate company name only; keep " (count)" always visible. */
const formatCompanyTickLabel = (fullLabel) => {
    const match = /^(.*) \((\d+)\)$/.exec(fullLabel);
    if (!match) return Helpers.truncate(fullLabel, Y_LABEL_MAX);
    return Helpers.truncate(match[1], Y_LABEL_MAX) + ' (' + match[2] + ')';
};

const TopCompanies = ({ parentWidth, EmployeeCompany, statecode, employeeTypes, industries, agg, university, state, props }) => {
    //check if reports is loading form shared link
    let isSharedReport = false;
    let params = useParams();

    if (params) {
        isSharedReport = params["link_id"] !== undefined && params["p1"] !== undefined && params["p2"] !== undefined;
    }
    
    const employeeProfileReportState = useSelector(state => state.newEmployeeReportProperties);

    //list of companies that don't pass the privacy threshold, this is being set in the useEffect function
    const [filteredArray, setFilteredArray] = useState([]);
    const [selectedType, setSelectedType] = useState("Graduate Student");
    const [selectedIndustry, setSelectedIndustry] = useState("");
    const [hasInsufficientData, setHasInsufficientData] = useState(false);

    const dispatch = useDispatch();

    useEffect(() => {
        if (employeeTypes.length > 0 && industries.length > 0) {
            if (selectedIndustry == "") {
                selectedIndustry = industries[0].type;
                setSelectedIndustry(industries[0].type);
            }

            let filteredByTypeAndIndustry = EmployeeCompany.filter(x => +x.statecode == statecode
                && x.employee == selectedType
                && (x.industry) == selectedIndustry);

            filteredByTypeAndIndustry = filteredByTypeAndIndustry.sort((a, b) => (a.median_salary < b.median_salary) ? 1 : -1);

            const nonePrivacyList = filteredByTypeAndIndustry.filter(x => x.count > 0);
            nonePrivacyList = nonePrivacyList.slice(0, 10);
            //companies = filteredByTypeAndIndustry.map(x => (x.company_name));

            setFilteredArray((_) => {
                const privacyList = filteredByTypeAndIndustry.filter(x => x.count == 0);
                privacyList = privacyList.sort((a, b) => (a.company_name < b.company_name) ? -1 : 1);

                return privacyList;
            });

            d3.select("#BarChart svg").remove();
            d3.select("#earnings-in-top-10-companies .er_tooltip").remove();
            setHasInsufficientData(nonePrivacyList.length === 0);
            if (nonePrivacyList.length > 0) {

                var margin = { top: 25, right: 75, bottom: 60, left: 300 },
                    width = (parentWidth+50 || 875) - margin.left - margin.right,
                    height = 320 - margin.top - margin.bottom;

                // append the svg object to the body of the page
                var svg = d3.select("#BarChart")
                    .append("svg")
                    .attr("width", width + margin.left + margin.right)
                    .attr("height", height + margin.top + margin.bottom)
                    .append("g")
                    .attr("transform",
                        "translate(" + margin.left + "," + margin.top + ")");

                const data = nonePrivacyList;
                const chartContainer = document.getElementById("earnings-in-top-10-companies");

                // Add X axis
                var x = d3.scaleLinear()
                    .domain([0, d3.max(data, function (d) {
                        return d.median_salary;
                    })])
                    .range([0, width]);

                svg.append("g")
                    .attr("transform", "translate(0," + height + ")")
                    .classed('tick_style_companies', true)
                    .call(d3.axisBottom(x).tickSizeOuter(0).tickFormat(x => `${(Helpers.billionNumberFormat(x))}`))
                    .selectAll("text")
                    .style("text-anchor", "middle");

                // Y axis
                var y = d3.scaleBand()
                    .range([0, height])
                    .domain(data.map(companyAxisLabel))
                    .padding(.1);

                var tooltipCompanies = d3.select("#earnings-in-top-10-companies").append("div")
                    .attr("class", "er_tooltip")
                    .style("opacity", 0);

                const companiesTooltipHtml = (row) => {
                    const company = Helpers.toTitleCase(row.company_name);
                    const earnings = `$${row.median_salary.toLocaleString()}`;
                    return `Company: ${company}<br/>Employee Count: ${row.count}<br/>Median Earnings: ${earnings}`;
                };

                const rowFromAxisLabel = (fullLabel) =>
                    data.find(d => companyAxisLabel(d) === fullLabel);

                const showCompaniesTooltip = (event, row) => {
                    if (!row) return;
                    const [px, py] = d3.pointer(event, chartContainer);
                    tooltipCompanies.style("opacity", .9);
                    tooltipCompanies.html(companiesTooltipHtml(row))
                        .style("left", (px + 10) + "px")
                        .style("top", (py + 10) + "px");
                };

                const hideCompaniesTooltip = () => {
                    tooltipCompanies.style("opacity", 0);
                };

                svg.append("g")
                    .classed('tick_style_companies', true)
                    .call(d3.axisLeft(y)
                        .tickSizeOuter(0)
                        .tickSize(0)
                        .tickPadding(10)
                        .tickFormat(d => formatCompanyTickLabel(d))
                    )
                    .selectAll(".tick text")
                    .style("cursor", "pointer")
                    .on("mouseover", (event, d) => {
                        showCompaniesTooltip(event, rowFromAxisLabel(d));
                    })
                    .on("mouseout", hideCompaniesTooltip);

                // color palette = one color per subgroup
                var color = d3.scaleOrdinal()
                    .domain(nonePrivacyList.map(c => c.company_name))
                    .range(schemeCategory10)

                //Bars
                svg.selectAll("myRect")
                    .data(data)
                    .enter()
                    .append("rect")
                    .attr("x", x(0)+1)
                    .attr("y", d => y(companyAxisLabel(d)) + 5)
                    .attr("width", d => x(d.median_salary))
                    .attr("height", y.bandwidth()-10)
                    //.attr("fill", d => color(d.company_name))
                    .attr("fill", "#ADD8E6")
                    .style("cursor", "pointer")
                    .on('mouseover', (event, d) => {
                        const dataValue = d ?? event.target?.__data__;
                        showCompaniesTooltip(event, dataValue);
                    })
                    .on("mouseout", hideCompaniesTooltip);


                svg.append("text")
                    .attr("text-anchor", "end")
                    .attr("x", width/2)
                    .attr("y", height + margin.top + 30)
                    .classed('axis_style', true)
                    .text("Median Earnings ($)");

                // Y axis label:
                svg.append("text")
                    .attr("text-anchor", "end")
                    .attr("transform", "rotate(-90)")
                    .attr("y", -margin.left + 20)
                    .attr("x", -margin.top - 80)
                    .classed('axis_style', true)
                    .text("Company (Count)")
            }

        }

    }, [statecode, employeeTypes, industries, selectedType, selectedIndustry, parentWidth])

    const handleType = (selectedType) => {
        setSelectedType(selectedType)
    }

    const handleIndustry = (selectedIndustry) => {
        setSelectedIndustry(selectedIndustry)
    }
    
    return (
        <Container>
            <ChartRoot id="earnings-in-top-10-companies">
                <TitleDiv>
                    <Heading3 bold center>{`Median Earnings of Research-Funded ${selectedType.replace("Student", "Students").replace("Postdoc", "Postdocs").replace("Undergraduate", "Undergraduates")} in ${selectedIndustry} (Top 10 Companies)`}</Heading3>
                </TitleDiv>
                {hasInsufficientData && (
                    <InsufficientDataMessage>
                        <strong>Insufficient data</strong>
                        <span>Try a different employee type or industry.</span>
                    </InsufficientDataMessage>
                )}
                <div id="BarChart"></div>
            </ChartRoot>
            <div>
                <ReportText><TextSpan bold>Employee type</TextSpan></ReportText>
                {(employeeTypes.length > 0) &&
                    employeeTypes.filter(x => { return x.selected == true }).map(
                        (item, idx) => (<ReportButton key={idx} active={selectedType == item.type} onClick={() => handleType(item.type)}>{item.type}</ReportButton>)
                    )
                }
            </div>
            <div>
                <ReportText><TextSpan bold>Industry</TextSpan></ReportText>
                {(industries.length > 0) &&
                    industries.filter(x => { return x.selected == true }).map(
                        (item, idx) => (<ReportButton key={idx} active={selectedIndustry == item.type} onClick={() => handleIndustry(item.type)}>{item.type}</ReportButton>)
                    )
                }
            </div>
            <p>This chart displays data for one employee type and one industry at a time, which are selected using the buttons below. Add or remove buttons using the Employee Types filter on the right.</p>
            <ReportText bold>* Employers with fewer than 5 employees are not displayed for privacy.</ReportText>
        </Container>
    );
}

const Container = styled.div`
    display: grid;
    gap: 2em;
`;

const ChartRoot = styled.div`
    position: relative;
`;

const InsufficientDataMessage = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
    text-align: center;
    margin: 3rem auto 1rem;
    padding: 0.75rem 1.25rem;
    max-width: fit-content;
    border: 1px solid #0A0B0A;
    color: #0A0B0A;
    font-size: 14px;
    font-style: normal;

    strong {
        font-size: 15px;
        font-style: normal;
        font-weight: 700;
        color: inherit;
    }

    span {
        font-style: normal;
        color: inherit;
    }
`;

export default TopCompanies;